import React, { useState } from "react";
import {
  IconButton,
  Menu,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogActions,
  DialogContent,
  Button,
  Grid,
  Typography,
} from "@material-ui/core";
import ArrowDropDownIcon from "@material-ui/icons/ArrowDropDown";
import KeyboardArrowLeftIcon from "@material-ui/icons/KeyboardArrowLeft";
import { useStyles } from './ProductDevelopmentActionsStyles';

/**
 * ProductDevelopmentActions Component
 * 
 * Similar to BatchColumnStatusRoute, provides actions menu for table rows.
 * Supports both regular dialog actions and QuickView actions.
 * 
 * @param {Object} props
 * @param {Object} props.row - Row data object
 * @param {Array} props.actions - Array of action objects with structure:
 *   {
 *     label: "Action Label",
 *     title: "Dialog Title",
 *     component: <Component />, // Optional: for dialog actions
 *     isQuickView: true, // Optional: flag for QuickView actions
 *     onClick: (row) => {}, // Optional: custom handler
 *     featureAccess: "featureKey", // Optional: feature access key
 *   }
 * @param {Object} props.featuresAccess - Feature access object for filtering actions
 * @param {Function} props.onActionClick - Optional callback when action is clicked
 */
const ProductDevelopmentActions = (props) => {
  const { row, actions = [], featuresAccess = {}, onActionClick } = props;
  const classes = useStyles();
  const [anchorEl, setAnchorEl] = React.useState(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedComponent, setSelectedComponent] = useState(null);
  const [selectedTitle, setSelectedTitle] = useState("");
  const [openQuickView, setOpenQuickView] = useState(false);
  const [selectedQuickViewAction, setSelectedQuickViewAction] = useState(null);

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleCloseMenu = () => {
    setAnchorEl(null);
  };

  const handleMenuItemClick = (action) => {
    // If custom onClick handler is provided, use it
    if (action.onClick) {
      action.onClick(row);
      handleCloseMenu();
      return;
    }

    // If it's a QuickView action
    if (action.isQuickView) {
      setSelectedQuickViewAction(action);
      setOpenQuickView(true);
    } else if (action.component) {
      // Regular dialog action
      setSelectedComponent(action.component);
      setSelectedTitle(action.title || action.label);
      setOpenDialog(true);
    }

    // Call optional callback
    if (onActionClick) {
      onActionClick(action, row);
    }

    handleCloseMenu();
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setSelectedComponent(null);
    setSelectedTitle("");
  };

  const handleCloseQuickView = () => {
    setOpenQuickView(false);
    setSelectedQuickViewAction(null);
  };

  // Filter actions based on feature access
  const filteredActions = React.useMemo(() => {
    if (!featuresAccess || Object.keys(featuresAccess).length === 0) {
      return actions;
    }

    return actions.filter((action) => {
      // If action has featureAccess key, check if user has access
      if (action.featureAccess) {
        return featuresAccess[action.featureAccess] !== false;
      }
      // If no featureAccess specified, show by default
      return true;
    });
  }, [actions, featuresAccess]);

  // If no actions available, return null or a placeholder
  if (!actions || actions.length === 0) {
    return null;
  }

  return (
    <div>
      <div className={classes.actionButton} onClick={handleClick}>
        <span className={classes.actionText}>Actions</span>
        <IconButton
          size="small"
          style={{ padding: "2px", marginLeft: "4px" }}
        >
          <ArrowDropDownIcon fontSize="small" />
        </IconButton>
      </div>

      <Menu
        anchorEl={anchorEl}
        keepMounted
        open={Boolean(anchorEl)}
        onClose={handleCloseMenu}
        classes={{ paper: classes.paper }}
        PopoverClasses={{ root: classes.popover }}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "right",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
        getContentAnchorEl={null}
      >
        {filteredActions.map((action, index) => (
          <MenuItem
            key={action.label || index}
            onClick={() => handleMenuItemClick(action)}
          >
            {action.label}
          </MenuItem>
        ))}
      </Menu>

      {/* Full-Screen Dialog for regular actions */}
      <Dialog fullScreen open={openDialog} onClose={handleCloseDialog}>
        <DialogActions>
          <Grid
            style={{
              display: "flex",
              justifyContent: "flex-start",
              flexDirection: "column",
              alignItems: "start",
            }}
          >
            <Grid
              style={{
                display: "flex",
                justifyContent: "flex-start",
                alignItems: "start",
                marginTop: "0.3rem",
              }}
            >
              <Button
                onClick={handleCloseDialog}
                style={{
                  color: "black",
                  fontWeight: 550,
                  textTransform: "none",
                }}
              >
                <KeyboardArrowLeftIcon fontSize="19" color="black" />
                Back
              </Button>
              <Typography
                variant="h6"
                style={{ fontWeight: "bold", marginLeft: "2rem" }}
              >
                {selectedTitle}
              </Typography>
            </Grid>
            <Grid>
              <div
                style={{
                  width: "97vw",
                  height: "1.5px",
                  backgroundColor: "#bdbdbd",
                  marginTop: "1rem",
                }}
              ></div>
            </Grid>
          </Grid>
        </DialogActions>
        <DialogContent>{selectedComponent}</DialogContent>
      </Dialog>

      {/* QuickView Dialog */}
      {openQuickView && selectedQuickViewAction && (
        <Dialog
          fullScreen
          open={openQuickView}
          onClose={handleCloseQuickView}
        >
          {selectedQuickViewAction.quickViewComponent ? (
            selectedQuickViewAction.quickViewComponent
          ) : (
            <DialogContent>
              <Typography variant="h6">{selectedQuickViewAction.title || selectedQuickViewAction.label}</Typography>
              <Typography variant="body2" style={{ marginTop: "1rem" }}>
                QuickView component will be rendered here. Pass quickViewComponent prop to action.
              </Typography>
              <Button onClick={handleCloseQuickView} style={{ marginTop: "1rem" }}>
                Close
              </Button>
            </DialogContent>
          )}
        </Dialog>
      )}
    </div>
  );
};

export default ProductDevelopmentActions;

